/**
 * Отклонения в подаче отдельных групп в LMS.
 *
 * Группа опознаётся по ИМЕНИ (`Group.name`, например `PY01-03-26`) — оно уже
 * приходит в обоих запросах (`AllCourses` и `groupForLms`) и уже доезжает до UI
 * как `groupName`, поэтому ни нового поля, ни похода на бэкенд здесь не нужно.
 * Тот же приём «конфиг по имени» уже используется в `courseIframeConfig.ts`.
 *
 * Модуль намеренно НЕ импортирует `context`: оттуда тянется i18n, иконки и
 * роутер, а этот файл должен оставаться чистым и тестируемым в изоляции.
 * Проверка роли (расписание прячем только студенту) живёт на вызывающей
 * стороне — там `UserRole` уже импортирован.
 */

export type GroupServiceInfo = {
  /** Номер услуги в BUR — печатается дословно. */
  serviceNumber: string;
  /** Срок услуги: даты хранятся отдельно, чтобы подпись локализовалась. */
  periodFrom: string;
  periodTo: string;
};

export type GroupOverride = {
  /** Имя группы ровно как в ERP. */
  name: string;
  /**
   * Прятать всю календарную механику: даты и время занятий, нумерацию занятий,
   * количество студентов и занятий. Применяется ТОЛЬКО к студенту.
   */
  hideSchedule?: boolean;
  /** Реквизиты услуги — показываются всем ролям. */
  service?: GroupServiceInfo;
};

export const GROUP_OVERRIDES: GroupOverride[] = [
  {
    name: 'PY01-03-26',
    hideSchedule: true,
    service: {
      serviceNumber: '2026/01/23/182536/3279166',
      periodFrom: '31.03.2026',
      periodTo: '28.08.2026',
    },
  },
];

/**
 * Совпадение ТОЧНОЕ (после trim + приведения регистра), а не `includes`: иначе
 * будущая `PY01-03-26-B` молча подхватила бы чужой режим.
 */
export const getGroupOverride = (groupName?: string | null): GroupOverride | null => {
  if (!groupName) {
    return null;
  }
  const normalized = groupName.trim().toLowerCase();
  return GROUP_OVERRIDES.find((group) => group.name.trim().toLowerCase() === normalized) || null;
};

/** Реквизиты услуги группы; от роли не зависят. */
export const getGroupService = (groupName?: string | null): GroupServiceInfo | null =>
  getGroupOverride(groupName)?.service || null;

/** Помечена ли группа как «без расписания». Роль проверяется отдельно. */
export const isHiddenScheduleGroup = (groupName?: string | null): boolean =>
  Boolean(getGroupOverride(groupName)?.hideSchedule);
